// exercise-library-api.js
// Functions for managing exercise library

import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  doc, 
  getDocs,
  query, 
  where,
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from './firebase-config';

/**
 * Create a new exercise in the library
 * @param {string} coachId - Coach's user ID
 * @param {object} exerciseData - { name, category, description, videoUrl, imageUrl, tips }
 */
export const createExercise = async (coachId, exerciseData) => {
  try {
    const docRef = await addDoc(collection(db, 'exercise_library'), {
      coach_id: coachId,
      name: exerciseData.name,
      category: exerciseData.category || 'general',
      description: exerciseData.description || '',
      video_url: exerciseData.videoUrl || '',
      image_url: exerciseData.imageUrl || '',
      tips: exerciseData.tips || '',
      created_at: Timestamp.now(),
      updated_at: Timestamp.now()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating exercise:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all exercises for a coach
 * @param {string} coachId - Coach's user ID
 */
export const getCoachExercises = async (coachId) => {
  try {
    const q = query(
      collection(db, 'exercise_library'),
      where('coach_id', '==', coachId)
      // Removed orderBy to avoid index requirement
    );
    const snapshot = await getDocs(q);
    const exercises = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort in JavaScript instead of Firestore
    exercises.sort((a, b) => a.name?.localeCompare(b.name) || 0);
    
    return exercises;
  } catch (error) {
    console.error('Error fetching exercises:', error);
    return [];
  }
};

/**
 * Update an exercise
 * @param {string} exerciseId - Exercise document ID
 * @param {object} updates - Fields to update
 */
export const updateExercise = async (exerciseId, updates) => {
  try {
    const exerciseRef = doc(db, 'exercise_library', exerciseId);
    await updateDoc(exerciseRef, {
      ...updates,
      updated_at: Timestamp.now()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating exercise:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete an exercise
 * @param {string} exerciseId - Exercise document ID
 */
export const deleteExercise = async (exerciseId) => {
  try {
    await deleteDoc(doc(db, 'exercise_library', exerciseId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting exercise:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get exercises accessible to an athlete (their coach's library)
 * @param {string} coachId - The athlete's coach ID
 */
export const getAthleteExercises = async (coachId) => {
  try {
    const q = query(
      collection(db, 'exercise_library'),
      where('coach_id', '==', coachId),
      orderBy('category', 'asc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching exercises for athlete:', error);
    return [];
  }
};
